/**
 * Product date presentation: always American style MM/DD/YYYY.
 * Wire/API values stay ISO (YYYY-MM-DD or ISO datetime).
 */

const EMPTY = '—'

/**
 * Format a calendar date for display (MM/DD/YYYY).
 * Accepts:
 * - date-only strings `YYYY-MM-DD` (no TZ shift)
 * - ISO datetimes (uses local calendar day)
 * - Date instances
 * Null/empty/invalid → `—` (or empty string if `empty` is '')
 */
export function formatDate(
  value: string | Date | null | undefined,
  empty: string = EMPTY,
): string {
  if (value == null || value === '') return empty

  if (typeof value === 'string') {
    const dateOnly = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|T)/)
    if (dateOnly) {
      const [, y, m, d] = dateOnly
      return `${m}/${d}/${y}`
    }
  }

  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return empty

  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const y = String(d.getFullYear())
  return `${m}/${day}/${y}`
}

/**
 * Format an instant for display: MM/DD/YYYY, h:mm AM/PM (US English).
 * Use for created/updated/issued timestamps.
 */
export function formatDateTime(
  value: string | Date | null | undefined,
  empty: string = EMPTY,
): string {
  if (value == null || value === '') return empty
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return empty

  const date = formatDate(d, empty)
  if (date === empty) return empty

  const time = d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
  return `${date}, ${time}`
}
